import React, { forwardRef, MouseEventHandler, Ref, useEffect } from 'react';
import { Dropdown } from 'react-bootstrap';
import { ApiNote, ApiQuickStatusComment, ApiSanctionAlert } from '../../../api';
import { AppState } from '../../../store/store';
import { getUserId } from '../../../auth/authReducer';
import { fetchQuickStatusComments } from './statusCommentsAction';
import { connect, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/pro-solid-svg-icons';
import { DropdownWrapper } from '../../../common/DropdownWrapper';
import { Button } from '@salvapp/ui-components';
import { ButtonProps } from '@salvapp/ui-components/build/button/button.types';
import confirmStatus from '../../../common/status/confirmStatus';

interface QuickStatusCommentsProps {
  alert?: ApiSanctionAlert;
  onSubmit: (values: ApiNote, status: string) => Promise<void>;
  showModal: (showModal: boolean) => void;
  caption?: string;
  withIcon?: boolean;
  hideTopComments?: boolean;
  btnSize?: ButtonProps['size'];
}

export const QuickStatusComments = ({
  alert,
  quickComments,
  onSubmit,
  showModal,
  caption,
  statuses,
  withIcon = false,
  btnSize = 'large',
}: Props) => {
  const dispatch = useDispatch();
  useEffect(() => {
    if (quickComments === null) {
      dispatch(fetchQuickStatusComments());
    }
  }, [fetchQuickStatusComments, quickComments]);

  const quickCommentLabel = (quickComment: ApiQuickStatusComment) => {
    return (
      <span>
        <b>{statuses.find((it) => it.code == quickComment.status.toString())?.textName}</b> • {quickComment.comment}
      </span>
    );
  };

  const submitComment = async (quickComment: ApiQuickStatusComment) => {
    const note: ApiNote = {
      sanctionAlertId: alert?.id,
      content: quickComment.comment,
    };
    const quickCommentStatus = [...statuses].reverse().find((it) => it.code == quickComment.status)!;

    await confirmStatus({
      onSubmit: () => onSubmit(note, quickComment.status),
      warnOnFinal: quickCommentStatus.isFinal,
      warnOnTruePositive: quickCommentStatus.isFinal && quickCommentStatus.isImportant,
      statusName: quickCommentStatus.textName,
      dispatch: dispatch,
    });
  };

  interface ToggleProps {
    onClick: MouseEventHandler;
  }

  const Toggle = forwardRef(({ onClick }: ToggleProps, ref: Ref<any>) => {
    const handleClick = (e: any) => {
      e.preventDefault();
      onClick(e);
    };

    return (
      (withIcon && (
        <Button dataTestId='addStatus' variant='secondary' size={btnSize} ref={ref} onClick={handleClick}>
          <FontAwesomeIcon icon={faUser} className='fe me-2' />
          {caption}
        </Button>
      )) || (
        <Button variant='white' size={btnSize} ref={ref} onClick={handleClick} className='dropdown dropdown-toggle'>
          {caption ?? (alert && statuses.find((it) => it.id == alert.statusId)?.textName)}
        </Button>
      )
    );
  });

  Toggle.displayName = 'Toggle';

  if (quickComments && quickComments.length > 0) {
    return (
      <DropdownWrapper>
        <Dropdown.Toggle as={Toggle} id='actions-dropdown' />
        <Dropdown.Menu>
          {quickComments.map((quickComment, index) => {
            return (
              <Dropdown.Item key={index} onClick={() => submitComment(quickComment)} data-testid={quickComment.comment}>
                {quickCommentLabel(quickComment)}
              </Dropdown.Item>
            );
          })}
          <Dropdown.Item onClick={() => showModal(true)}>Custom note and status...</Dropdown.Item>
        </Dropdown.Menu>
      </DropdownWrapper>
    );
  } else {
    return (
      <Button variant='white' size={btnSize} className='dropdown dropdown-toggle' onClick={() => showModal(true)}>
        {caption ?? (alert && statuses.find((it) => it.id == alert.statusId)?.textName)}
      </Button>
    );
  }
};

const mapStateToProps = (state: AppState) => ({
  quickComments: state.statusComments.quickComments,
  userId: getUserId(state),
  statuses: state.screeningAlertStatuses.statuses,
});

type Props = ReturnType<typeof mapStateToProps> & QuickStatusCommentsProps;

export default connect(mapStateToProps)(QuickStatusComments);
